import { basketTableTotalTypes, questionScopeCategories, questionSelectorOptions } from '/business/shared/util/Statics';
import type { i18n as I18nInstance } from 'i18next';
import type { BasketTableAnswerItem } from '/index';
import formatNumber from '/shared/functions/formatNumber';

import useDeicingSchema from '/business/shared/components/questions/scope1/_hooks/useDeicingSchema';
import SnowflakeIcon from '/business/shared/images/icons/icon-deicing.svg';
import initiativeCardBackground from '/business/shared/images/initiatives/deicing-card-1x.png';
import initiativeBackground from '/business/shared/images/initiatives/deicing-banner@1x.png';
import educationContentJson from '/business/shared/components/questions/scope1/_education/deicing.json';
import useFormatSharedCustomScope1 from '/business/shared/components/pages/question/formats/customDataFormats/useFormatSharedCustomScope1';
import useFormatSharedSupplierScope1Ver2 from '/business/shared/components/pages/question/formats/supplierDataFormats/useFormatSharedSupplierScope1Ver2';
import ReportingPeriod from '/business/shared/components/widgets/reporting-period/ReportingPeriod';
import StyledParagraph from '/shared/styled/components/StyledParagraph';
import StyledInfoFlash from '/shared/styled/StyledInfoFlash';
import { bottomContentsSupplier } from '/business/shared/components/questions/scope3/_education/bottomScope3';

export const getConfigureDeicing = ({
  i18n,
  hasScopeBasedOrganisation = true,
  reductionCardsSlides = ['slide1', 'slide2', 'slide3', 'slide4'],
}: {
  hasScopeBasedOrganisation?: boolean;
  i18n: I18nInstance;
  reductionCardsSlides?: string[];
}) => {
  return {
    id: 'deicing',
    scopeCategory: hasScopeBasedOrganisation
      ? questionScopeCategories.SCOPE_1
      : questionScopeCategories.BUILDING_EMISSIONS,
    isCompletePropName: 'isDeicingComplete',
    apiKey: 'calculation-deicing',
    title: i18n.t('business:Questions.calculations.deicing.title'),
    primaryI18nKey: 'business:QuestionPage.forms.emissionSelector.deicing',
    subTextI18nKey: 'business:QuestionPage.forms.emissionSelector.deicingTip',
    overviewPageTitleI18nKey: 'business:QuestionPage.forms.emissionSelector.deicing',
    reductionPageKey: 'deicing',
    reductionPageCards: reductionCardsSlides,
    icon: SnowflakeIcon,
    useHookLoader: useDeicingSchema,
    initiative: {
      formatForInitiativeSummary: () => {
        return {
          isShowingReportingPeriod: false,
          isShowingReportingPeriodNoReduction: false,
          category: false,
          description: false,
          descriptionNoReduction: false,
        };
      },
      getVisuals: ({ isBpOffering }: { isBpOffering: boolean }) => {
        if (!isBpOffering) {
          return {
            description: false,
            background: initiativeBackground,
            useMask: false,
            cardBackground: initiativeCardBackground,
          };
        }
        if (isBpOffering) {
          return {
            description: false,
            background: initiativeBackground,
            useMask: false,
            cardBackground: initiativeCardBackground,
          };
        }
      },
    },
    table: {
      title: i18n.t('business:Questions.calculations.deicing.table.title'),
      calculateTotal: (allItems: BasketTableAnswerItem[]) => allItems.length,
      totalType: basketTableTotalTypes.COUNT,
      headings: [
        i18n.t('business:Questions.calculations.deicing.table.heading1'),
        i18n.t('business:Questions.calculations.deicing.table.heading2'),
        i18n.t('business:Questions.calculations.deicing.table.heading3'),
      ],
      cells: [
        'chemicalType',
        ({ quantity, chemicalUnit = 'units' }: { quantity: number; chemicalUnit?: string }) =>
          `${quantity} ${i18n.t('business:emissionFactors.volumes.'.concat(chemicalUnit), chemicalUnit)}`,
        ({ tonnage }: { tonnage: number }) => `${formatNumber(tonnage)} tCO\u2082e`,
      ],
      isAnswerEditable: true,
      supplier: {
        columns: ['emissionSource', 'supplierName', 'scope1orAnd3Intensity', 'quantity', 'carbonEmissions'],
      },
      custom: {
        columns: ['emissionSource', 'intensityValue', 'unitOfMeasurement', 'quantity', 'carbonEmissions'],
      },
    },
    details: {
      title: 'chemicalType',
      items: [
        'chemicalType',
        ({ quantity, chemicalUnit = 'units' }: { quantity: number; chemicalUnit?: string }) =>
          `${quantity} ${i18n.t('business:emissionFactors.volumes.'.concat(chemicalUnit), chemicalUnit)}`,
        ({ tonnage }: { tonnage: number }) => `${formatNumber(tonnage)} ${i18n.t('business:Snippets.tonnes')}`,
        {
          value: 'dataSource',
          title: i18n.t('business:Questions.calculations.question2.details.title'),
        },
      ],
    },
    selector: {
      injectQuestionBranchSelector: true,
      questionBranchEnableCustomData: true,
      questionBranchEnableSupplierData: true,
      para: 'To calculate emissions associated with de-icing chemicals used on-site by your business, you can use a combination of activity data, supplier data, and custom data.',
      options: [
        {
          title: questionSelectorOptions.ACTIVITY,
          requiredData: 'Type of de-icing chemical, quantity used during the reporting period.',
        },
        {
          title: questionSelectorOptions.SUPPLIER,
          requiredData: 'Type of de-icing chemical, supplier name and intensity data/absolute emissions, quantity.',
        },
        {
          title: questionSelectorOptions.CUSTOM,
          requiredData: 'Emission source, intensity data/absolute emissions, quantity.',
        },
      ],
    },
    education: {
      hasEducationWidget: true,
      educationContent: educationContentJson,
      bottomContentsSupplier: bottomContentsSupplier,
    },
    useQuestionBranchSupplierLoader: () =>
      useFormatSharedSupplierScope1Ver2({
        questionFormatName: 'deicing',
        emissionSourcePlaceholder: 'Select a de-icing chemical',
      }),
    useQuestionBranchCustomLoader: () => useFormatSharedCustomScope1({ questionFormatName: 'deicing' }),
    supplierAndCustomSubheader: (
      <>
        <StyledParagraph $largeText $hasExtraMarginBottom>
          Enter data about the de-icing chemical(s) that have been used by your business during the reporting period:{' '}
          <ReportingPeriod $green />
        </StyledParagraph>
        <StyledInfoFlash>
          <div>For more information, refer to the "Help" section for detailed guidance.</div>
        </StyledInfoFlash>
      </>
    ),
    activitySubheader: (
      <>
        <StyledParagraph $largeText $hasExtraMarginBottom>
          What de-icing chemical(s) have been used by your business during the reporting period:{' '}
          <ReportingPeriod $green />
        </StyledParagraph>
        <StyledInfoFlash>
          <div>For more information, refer to the "Help" section for detailed guidance.</div>
        </StyledInfoFlash>
      </>
    ),
  };
};
